import ModalSurface from '../shared/ModalSurface';
import '../../styles/restScreens.css';
import React, { useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';

const BRANDS = ['General', 'VW', 'VaLyn', 'The Ride', 'smarTEK', 'Po1'];
const PRIORITIES = ['Critical', 'Urgent', 'Important', 'Normal', 'Low'];

// ============================================================
// CreateTaskModal — quick-add form. Owner/assignee default to the
// current user (single-tenant, single-user reality today per
// Module_13_Task_Management_v1_02.md §13 Gate 5); a real
// assignee picker is future state once there's more than one user
// in a tenant to assign to.
// ============================================================

export default function CreateTaskModal({ userId, theme = 'light', onClose, onCreated }) {
  const [taskName, setTaskName] = useState('');
  const [workspaceTag, setWorkspaceTag] = useState('General');
  const [priority, setPriority] = useState('Normal');
  const [bucketName, setBucketName] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState('');
  const [orgId, setOrgId] = useState(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let active = true;
    async function loadOwnership() {
      const { data: organization, error: orgError } = await supabaseClient.rpc('auth_org_id');
      if (!active) return;
      if (orgError || !organization) {
        setLoadError(orgError?.message || 'Could not verify your organization.');
        return;
      }
      const { data, error } = await supabaseClient.from('entities')
        .select('id, name').eq('org_id', organization).in('level_type', ['company', 'market']).order('name');
      if (!active) return;
      if (error) {
        setLoadError(error.message);
        return;
      }
      setOrgId(organization);
      setEntities(data || []);
    }
    loadOwnership().catch(error => { if (active) setLoadError('Could not load ownership: ' + error.message); });
    return () => { active = false; };
  }, []);

  async function handleCreate() {
    if (!taskName.trim() || !entityId || !orgId) return;
    setSaving(true);
    const { error } = await supabaseClient.from('tasks').insert([{
      task_name: taskName.trim(),
      org_id: orgId,
      entity_id: entityId,
      workspace_tag: workspaceTag,
      priority,
      bucket_name: bucketName || null,
      due_date: dueDate || null,
      owner_id: userId,
      assignee_id: userId,
    }]);
    setSaving(false);
    if (error) {
      alert('Error creating task: ' + error.message);
      return;
    }
    onCreated && onCreated();
  }

  const inputClass = `w-full text-sm rounded-md border px-3 py-2 ${
    theme === 'dark' ? 'bg-gray-900 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-800'
  }`;
  const labelClass = `text-xs font-medium uppercase tracking-wide mb-1 block ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`;

  return (
    <ModalSurface closeOnEscape={false} onClose={onClose} label="New task" overlayClassName="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onOverlayClick={onClose} panelClassName={`w-full max-w-md rounded-lg shadow-xl p-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`} panelProps={{"data-m6-layout":"tasks","data-m6-component":"CreateTaskModal"}}>
        <h2 className={`text-lg font-bold mb-4 ${'text-[var(--ink)]'}`}>New Task</h2>

        <div className="space-y-3">
          {loadError && <p role="alert" className="text-sm text-red-600">{loadError}</p>}
          <div>
            <label htmlFor="task-entity" className={labelClass}>Company / Market</label>
            <select id="task-entity" value={entityId} onChange={e => setEntityId(e.target.value)} className={inputClass}>
              <option value="">Choose ownership</option>
              {entities.map(entity => <option key={entity.id} value={entity.id}>{entity.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Task Name</label>
            <input
              autoFocus
              value={taskName}
              onChange={(e) => setTaskName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
              className={inputClass}
              placeholder="What needs to happen?"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelClass}>Brand</label>
              <select value={workspaceTag} onChange={(e) => setWorkspaceTag(e.target.value)} className={inputClass}>
                {BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Priority</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className={inputClass}>
                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Bucket</label>
              <input value={bucketName} onChange={(e) => setBucketName(e.target.value)} className={inputClass} placeholder="Optional" />
            </div>
            <div>
              <label className={labelClass}>Due Date</label>
              <input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className={inputClass} />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="text-sm px-4 py-2 rounded-md border border-gray-400 text-gray-500 hover:bg-gray-100">
            Cancel
          </button>
          <button
            onClick={handleCreate}
            disabled={saving || !taskName.trim() || !entityId || !orgId}
            className="text-sm px-4 py-2 rounded-md bg-[var(--btn-fill)] text-[var(--btn-ink)] hover:bg-[var(--btn-fill-hover)] disabled:opacity-50"
          >
            {saving ? 'Creating...' : 'Create Task'}
          </button>
        </div>
      </ModalSurface>
  );
}
